import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';

import {
  BulkUploadJobResponse,
  BulkUploadProgress,
  BulkUploadProgressEvent,
  BulkUploadRequest,
  BulkUploadResponse,
} from '../interfaces/stock.interface';
import { CenterLookupDto } from 'src/app/shared/interfaces/center.interface';

import { StoreStockLotExportItem } from './store-stock-lots-export.model';
import { buildCsv, CsvColumn } from 'src/app/core/utils/download-csv';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { ImportRowsResult } from '../components/bulk-upload-modal/bulk-upload-modal.component';

export interface GetStockResponse {
  items: unknown[]; // no tienes el endpoint en este fichero; mantener safe
  metadata: GetStockMetadata;
}

interface GetStockMetadata {
  continuationToken: string | null;
  currentPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface AvailableStockProduct {
  productId: number;
  productName: string;
  stores: AvailableStockStore[];
}

export interface AvailableStockStore {
  storeId: number;
  storeName: string;
  availableStock: number;
}

type ExportStockResponse = HttpResponse<Blob>;

interface ExportStockRequestBody {
  ProductIds: number[];
  StoreIds: number[];
}


interface BackendErrorEnvelope {
  error?: unknown;
  details?: unknown;
}



@Injectable({ providedIn: 'root' })
export class StockService {
  private readonly baseUrl = environment.cafler_backoffice_back_url;
  private readonly bulkUploadUrl = `${this.baseUrl}/bulk-upload`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly snackbarService: SnackbarService,
    private readonly analytics: AnalyticsService

  ) { }



  /**
   * Fetches the lightweight center list the current user is allowed to see.
   * Used by the bulk-upload modal to populate the GroupAdmin center selector.
   * Endpoint requires role SuperAdmin or GroupAdmin; CenterAdmin receives 403.
   */
  getCentersLookup(): Observable<CenterLookupDto[]> {
    return this.authRequest(
      (headers) => this.http.get<CenterLookupDto[]>(`${this.baseUrl}/centers/lookup`, { headers }),
      'Error al cargar los centros'
    );
  }

  previewBulkUpload(request: BulkUploadRequest): Observable<BulkUploadResponse> {
    const body = this.buildBulkUploadBody(request, false);
    return this.authRequest(
      (headers) => this.http.post<BulkUploadResponse>(`${this.bulkUploadUrl}/process`, body, { headers }),
      'Error al procesar la vista previa de la carga masiva'
    );
  }

  confirmBulkUpload(request: BulkUploadRequest, importInfo: ImportRowsResult): Observable<BulkUploadJobResponse> {
    const body = this.buildBulkUploadBody(request, true);
    return this.authRequest(
      (headers) => this.http.post<BulkUploadJobResponse>(`${this.bulkUploadUrl}/process`, body, { headers }),
      'Error al confirmar la carga masiva'
    ).pipe(
      tap((resp: BulkUploadJobResponse) => {
        this.analytics.track(AnalyticsEvent.StockImportCsvSuccess, {
          ...this.analytics.globalProps(),
          file_rows: importInfo.amountOfFiles,
          valid_rows: importInfo.validRows,
          invalid_rows: importInfo.invalidRows,
          warehouse_id: request.storeId,
        });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.StockImportCsvFail, {
          ...this.analytics.globalProps(),
          warehouse_id: request.storeId,
          error_code: errorCode,
          error_message: errorMessage,
          http_status: err.status,
        });

        return throwError(() => err);
      })
    );
  }



  exportStock(productIds?: number[], storeIds?: number[]): Observable<ExportStockResponse> {
    const body: ExportStockRequestBody = {
      ProductIds: (productIds ?? []).filter(isFiniteNumber),
      StoreIds: (storeIds ?? []).filter(isFiniteNumber),
    };

    return this.authRequest(
      (headers) =>
        this.http.post(`${this.baseUrl}/store-stock/export`, body, {
          headers,
          responseType: 'blob',
          observe: 'response',
        }),
      'Error al exportar el stock'
    );
  }

  connectBulkUploadProgress(jobId: string): Observable<BulkUploadProgressEvent> {
    return this.authRequest(
      (headers) => this.createServerSentEventStream(headers, jobId),
      'Error al conectar con el progreso de carga'
    );
  }

  private createServerSentEventStream(
    headers: HttpHeaders,
    jobId: string
  ): Observable<BulkUploadProgressEvent> {
    return new Observable((observer) => {
      const progressUrl = this.buildBulkUploadProgressUrl(jobId);
      const authHeader = headers.get('Authorization');
      const controller = new AbortController();

      const startStream = async () => {
        try {
          const response = await fetch(progressUrl, {
            method: 'GET',
            headers: {
              Authorization: authHeader || '',
              'ngrok-skip-browser-warning': 'true',
            },
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error(`SSE request failed with status ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('No SSE body reader available');
          }

          await this.readSseStream(reader, observer);
        } catch (error) {
          if (!controller.signal.aborted) {
            observer.error(error);
          }
        }
      };

      startStream();

      return () => controller.abort();
    });
  }


  private authRequest<T>(fn: (headers: HttpHeaders) => Observable<T>, errorMsg?: string): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        return fn(headers);
      }),
      catchError((err: unknown) => {
        if (errorMsg) this.snackbarService.showError(errorMsg);
        return throwError(() => err);
      })
    );
  }

  private buildBulkUploadBody(request: BulkUploadRequest, saveToDatabase: boolean): BulkUploadRequest {
    return {
      categoryId: request.categoryId,
      centerId: request.centerId ?? null,
      storeId: request.storeId,
      replaceStock: request.replaceStock,
      saveToDatabase,
      products: request.products,
    };
  }


  private buildBulkUploadProgressUrl(jobId: string): string {
    const params = new URLSearchParams();
    if (jobId) params.set('jobId', jobId);
    const query = params.toString();
    return query ? `${this.bulkUploadUrl}/progress?${query}` : `${this.bulkUploadUrl}/progress`;
  }

  private safeParseJson<T>(payload: string): T | null {
    try {
      return JSON.parse(payload) as T;
    } catch {
      return null;
    }
  }

  private async readSseStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    observer: { next: (value: BulkUploadProgressEvent) => void; complete: () => void; error: (err: unknown) => void }
  ): Promise<void> {
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let currentEvent = 'message';
    let dataBuffer = '';

    const flushEvent = () => {
      if (!dataBuffer) return;

      const payload = dataBuffer.trimEnd();
      dataBuffer = '';

      if (currentEvent === 'progress') {
        const data = this.safeParseJson<BulkUploadProgress>(payload);
        if (data) observer.next({ type: 'progress', data });
      }

      if (currentEvent === 'completed') {
        const data = this.safeParseJson<BulkUploadResponse>(payload);
        if (data) {
          observer.next({ type: 'completed', data });
          observer.complete();
        }
      }

      currentEvent = 'message';
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let lineEndIndex = buffer.indexOf('\n');
      while (lineEndIndex !== -1) {
        const line = buffer.slice(0, lineEndIndex).replace(/\r$/, '');
        buffer = buffer.slice(lineEndIndex + 1);

        if (line.startsWith('event:')) {
          currentEvent = line.replace('event:', '').trim();
        } else if (line.startsWith('data:')) {
          dataBuffer += line.replace('data:', '').trim() + '\n';
        } else if (line.trim() === '') {
          flushEvent();
        }

        lineEndIndex = buffer.indexOf('\n');
      }
    }
  }

}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}